import React, { useState } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form } from 'react-bootstrap';
import { 
  Search, 
//   Filter, 
  Package, 
  DollarSign, 
  ShoppingCart, 
  AlertTriangle, 
  PlusCircle, 
  Edit2,
  Trash2,
  ArrowUpDown,
//   Tag,
//   Boxes,
//   User
} from 'lucide-react';
import './Products.css';

// Sample data - in a real app this would come from an API
const initialProducts = [
  {
    id: 1,
    name: "Premium Wireless Headphones",
    image: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&q=80",
    category: "Electronics",
    price: 199.99,
    stock: 45,
    seller: "TechGear Pro",
    status: "In Stock",
    sku: "HDP-001",
    lastUpdated: "2024-03-10"
  },
  {
    id: 2,
    name: "Ergonomic Office Chair",
    image: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&q=80",
    category: "Furniture",
    price: 299.99,
    stock: 12,
    seller: "OfficePro Solutions",
    status: "Low Stock",
    sku: "CHR-002",
    lastUpdated: "2024-03-09"
  },
  {
    id: 3,
    name: "Smart Watch Series X",
    image: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&q=80",
    category: "Electronics",
    price: 399.99,
    stock: 0,
    seller: "TechGear Pro",
    status: "Out of Stock",
    sku: "WTC-003",
    lastUpdated: "2024-03-08"
  }
];

const categories = ["All", "Electronics", "Furniture", "Clothing", "Books", "Sports"];

function ProductDashboard() {
  const [products, setProducts] = useState(initialProducts);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showModal, setShowModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);

  // Stats calculation
  const stats = [
    {
      title: "Total Products",
      value: products.length,
      icon: Package,
      color: "primary"
    },
    {
      title: "Total Value",
      value: `$${products.reduce((acc, curr) => acc + (curr.price * curr.stock), 0).toLocaleString()}`,
      icon: DollarSign,
      color: "success"
    },
    {
      title: "Low Stock Items",
      value: products.filter(p => p.stock > 0 && p.stock <= 15).length,
      icon: AlertTriangle,
      color: "warning"
    },
    {
      title: "Out of Stock",
      value: products.filter(p => p.stock === 0).length,
      icon: ShoppingCart,
      color: "danger"
    }
  ];

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredProducts = products
    .filter(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (selectedCategory === "All" || product.category === selectedCategory)
    )
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      
      if (sortConfig.direction === 'asc') {
        return a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
      } else {
        return a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
      }
    });

  const handleOpenModal = (product = null) => {
    setCurrentProduct(product);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setCurrentProduct(null);
    setShowModal(false);
  };

  const handleSaveProduct = (event) => {
    event.preventDefault();
    // Add logic to save product
    handleCloseModal();
  };

  return (
    <Container fluid className="p-4 bg-light min-vh-100">
      {/* Header */}
      <div className="mb-4">
        <h1 className="h3 mb-0">Product Management</h1>
        <p className="text-muted">Manage your product inventory and listings</p>
      </div>

      {/* Stats Grid */}
      <Row className="g-4 mb-4">
        {stats.map((stat, index) => (
          <Col key={index} xs={12} md={6} lg={3}>
            <Card className="stat-card h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className={`icon-container bg-${stat.color}-subtle`}>
                    <stat.icon size={24} className={`text-${stat.color}`} />
                  </div>
                  <span className="h4 mb-0">{stat.value}</span>
                </div>
                <div className="text-muted small">{stat.title}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters and Search */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <div className="position-relative">
                <Search className="position-absolute top-50 translate-middle-y ms-3 text-muted" size={20} />
                <Form.Control
                  type="text"
                  placeholder="Search products..."
                  className="ps-5"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </Col>
            <Col md={6} className="d-flex gap-3 justify-content-md-end mt-3 mt-md-0">
              <Form.Select
                className="w-auto"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </Form.Select>
              <Button variant="primary" onClick={() => handleOpenModal()}>
                <PlusCircle size={20} className="me-2" />
                Add Product
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Products Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <div className="table-responsive">
            <Table hover className="table-products">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="cursor-pointer" onClick={() => handleSort('category')}>
                    <div className="d-flex align-items-center gap-2">
                      Category
                      <ArrowUpDown size={16} />
                    </div>
                  </th>
                  <th className="cursor-pointer" onClick={() => handleSort('price')}>
                    <div className="d-flex align-items-center gap-2">
                      Price
                      <ArrowUpDown size={16} />
                    </div>
                  </th>
                  <th className="cursor-pointer" onClick={() => handleSort('stock')}>
                    <div className="d-flex align-items-center gap-2">
                      Stock
                      <ArrowUpDown size={16} />
                    </div>
                  </th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <img 
                          src={product.image} 
                          alt="" 
                          className="product-image rounded me-3"
                        />
                        <div>
                          <div className="fw-medium">{product.name}</div>
                          <small className="text-muted">SKU: {product.sku}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-primary-subtle text-primary">
                        {product.category}
                      </span>
                    </td>
                    <td>${product.price.toFixed(2)}</td>
                    <td>{product.stock}</td>
                    <td>
                      <span className={`badge ${
                        product.stock === 0
                          ? 'bg-danger-subtle text-danger'
                          : product.stock <= 15
                          ? 'bg-warning-subtle text-warning'
                          : 'bg-success-subtle text-success'
                      }`}>
                        {product.status}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="link" 
                          className="p-0 text-primary"
                          onClick={() => handleOpenModal(product)}
                        >
                          <Edit2 size={18} />
                        </Button>
                        <Button 
                          variant="link" 
                          className="p-0 text-danger"
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {currentProduct ? 'Edit Product' : 'Add New Product'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveProduct}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    defaultValue={currentProduct?.name}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Select defaultValue={currentProduct?.category}>
                    {categories.filter(cat => cat !== 'All').map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Price</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    defaultValue={currentProduct?.price}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Stock</Form.Label>
                  <Form.Control
                    type="number"
                    defaultValue={currentProduct?.stock}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>SKU</Form.Label>
                  <Form.Control
                    type="text"
                    defaultValue={currentProduct?.sku}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Seller</Form.Label>
                  <Form.Control
                    type="text"
                    defaultValue={currentProduct?.seller}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Image URL</Form.Label>
                  <Form.Control
                    type="url"
                    defaultValue={currentProduct?.image}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default ProductDashboard;